import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { ArrowLeft, CreditCard, Check, Shield, Zap, AlertTriangle, Clock, X, Lock, Loader2, ExternalLink, FileText, Settings2, Webhook, Gift, Ticket } from 'lucide-react'
import { useAuth, type OrgTier } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/use-toast'
import {
  simulateAndProcessWebhook,
  getSubscriptionState,
  subscribeToSubscriptionEvents,
  checkGracePeriodExpiry,
  type SubscriptionState,
  type StripeWebhookEvent,
} from '@/lib/subscriptionEventsStore'
import { DEMO_AUTH_ENABLED } from '@/lib/env'
import { createCheckoutSession, createPortalSession } from '@/lib/stripeClient'
import { useActiveProGrantQuery, useRedeemInviteCodeMutation } from '@/data/inviteCodes'

const tierDetails: Record<OrgTier, { name: string; price: string; features: string[] }> = {
  free: {
    name: 'Free',
    price: '$0',
    features: [
      '2 active drops',
      '50MB per contributor',
      '2 week retention',
      'Downscaled photos',
    ],
  },
  pro: {
    name: 'Pro',
    price: '$9',
    features: [
      '10 active drops',
      '1GB per contributor',
      'Perpetual retention',
      'Full resolution',
      'Custom branding',
    ],
  },
  business: {
    name: 'Business',
    price: '$49',
    features: [
      '1000 active drops',
      '20GB per contributor',
      'Perpetual retention',
      'Full resolution',
      'Custom branding',
      'Team management',
    ],
  },
}

// Grace period in days for downgrades
const GRACE_PERIOD_DAYS = 7

export default function BillingPage() {
  const { organization, hasPermission, setOrgTier, requestDowngrade, cancelDowngrade } = useAuth()
  const { toast } = useToast()
  const [downgradeModalOpen, setDowngradeModalOpen] = useState(false)
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false)
  const [billingPortalOpen, setBillingPortalOpen] = useState(false)
  const [targetTier, setTargetTier] = useState<OrgTier | null>(null)
  const [subscriptionState, setSubscriptionState] = useState<SubscriptionState>(getSubscriptionState())
  const [lastWebhookEvent, setLastWebhookEvent] = useState<StripeWebhookEvent | null>(null)
  const [inviteCode, setInviteCode] = useState('')

  const activeGrantQuery = useActiveProGrantQuery(organization?.id)
  const activeGrant = activeGrantQuery.data ?? null
  const redeemMutation = useRedeemInviteCodeMutation()

  const handleRedeemCode = async () => {
    const code = inviteCode.trim()
    if (!code) return
    try {
      const result = await redeemMutation.mutateAsync(code)
      const until = result.expires_at
        ? new Date(result.expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        : null
      toast({
        title: 'Pro unlocked!',
        description: until
          ? `You now have Pro free until ${until}. Refreshing…`
          : 'You now have Pro. Refreshing…',
      })
      setInviteCode('')
      // Org tier is hydrated at sign-in bootstrap; reload so the new tier + limits apply.
      window.setTimeout(() => window.location.reload(), 1200)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not redeem code',
        description: err instanceof Error ? err.message : 'An error occurred.',
      })
    }
  }

  // Stripe Checkout returns the customer here via success_url/cancel_url. The
  // subscription tier itself is flipped asynchronously by the checkout.session.completed
  // webhook, which can land after this redirect — so acknowledge the payment explicitly
  // rather than letting the customer stare at a still-"Free" plan and assume it failed.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    const status = searchParams.get('status')
    if (status !== 'success' && status !== 'cancelled') return

    if (status === 'success') {
      toast({
        title: 'Payment received',
        description: 'Thanks! Your upgrade is being activated — this can take a few seconds to appear.',
      })
    } else {
      toast({
        title: 'Checkout cancelled',
        description: 'No charge was made. You can upgrade whenever you are ready.',
      })
    }

    // Drop the param so a refresh or back-navigation does not replay the toast.
    searchParams.delete('status')
    setSearchParams(searchParams, { replace: true })
  }, [searchParams, setSearchParams, toast])

  // Subscribe to webhook events
  useEffect(() => {
    // Check for grace period expiry on load
    checkGracePeriodExpiry()

    // Subscribe to future events
    const unsubscribe = subscribeToSubscriptionEvents((event, state) => {
      console.log('[BillingPage] Webhook event received:', event.type)
      setSubscriptionState(state)
      setLastWebhookEvent(event)

      // Show toast notification for important events
      if (event.type === 'customer.subscription.deleted') {
        toast({
          title: 'Subscription Cancelled',
          description: 'Your subscription has been cancelled. You are now on the Free plan.',
          variant: 'destructive',
        })
        setOrgTier('free')
      } else if (event.type === 'customer.subscription.updated' && event.data.object.cancel_at_period_end) {
        const endDate = new Date(event.data.object.current_period_end * 1000)
        toast({
          title: 'Cancellation Scheduled',
          description: `Your subscription will be cancelled on ${endDate.toLocaleDateString()}. Grace period is active.`,
        })
      } else if (event.type === 'invoice.payment_failed') {
        toast({
          title: 'Payment Failed',
          description: 'Your payment failed. Please update your payment method.',
          variant: 'destructive',
        })
      }
    })

    return () => unsubscribe()
  }, [toast, setOrgTier])

  // Extra check in case they somehow get here
  if (!hasPermission('manage_billing')) {
    return (
      <div className="space-y-8">
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-8 text-center">
          <Shield className="h-12 w-12 text-destructive mx-auto" />
          <h2 className="mt-4 text-xl font-semibold text-destructive">Access Denied</h2>
          <p className="mt-2 text-muted-foreground">
            You don't have permission to manage billing settings.
          </p>
          <Button asChild className="mt-4">
            <Link to="/dashboard">Return to Dashboard</Link>
          </Button>
        </div>
      </div>
    )
  }

  const currentTier = organization?.tier || 'free'
  const pendingDowngrade = organization?.pendingDowngrade

  const handleDowngradeClick = (tier: OrgTier) => {
    setTargetTier(tier)
    if (!DEMO_AUTH_ENABLED) {
      void handleManageBilling()
      return
    }
    setDowngradeModalOpen(true)
  }

  const handleUpgradeClick = async (tier: OrgTier) => {
    setTargetTier(tier)
    if (DEMO_AUTH_ENABLED) {
      // Demo mode: keep the simulated checkout modal flow
      setCheckoutModalOpen(true)
      return
    }
    if (!organization?.id) {
      toast({ title: 'Error', description: 'No organization loaded.', variant: 'destructive' })
      return
    }
    if (tier !== 'pro' && tier !== 'business') return
    try {
      await createCheckoutSession(organization.id, tier)
    } catch (err) {
      toast({
        title: 'Checkout failed',
        description: err instanceof Error ? err.message : 'Could not start checkout.',
        variant: 'destructive',
      })
    }
  }

  const handleManageBilling = async () => {
    if (DEMO_AUTH_ENABLED) {
      setBillingPortalOpen(true)
      return
    }
    if (!organization?.id) return
    try {
      await createPortalSession(organization.id)
    } catch (err) {
      toast({
        title: 'Portal unavailable',
        description: err instanceof Error ? err.message : 'Could not open billing portal.',
        variant: 'destructive',
      })
    }
  }

  const handleCheckoutComplete = (tier: OrgTier) => {
    // Simulate webhook event for subscription creation
    const newState = simulateAndProcessWebhook('customer.subscription.created', {
      tier: tier as 'pro' | 'business',
      status: 'active',
    })

    console.log('[Webhook] Subscription created via checkout:', newState)

    setOrgTier(tier)
    setCheckoutModalOpen(false)
    toast({
      title: 'Plan upgraded',
      description: `You are now on the ${tierDetails[tier].name} plan. Your new limits are now active.`,
    })
  }

  const handleCancelDowngrade = async () => {
    try {
      await cancelDowngrade()
      toast({
        title: 'Downgrade cancelled',
        description: 'Your pending downgrade has been cancelled.',
      })
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to cancel downgrade. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }

  return (
    <div className="space-y-8">
      {/* Back button */}
      <Link
        to="/settings"
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Settings
      </Link>

      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold">Billing & Subscription</h1>
        <p className="text-muted-foreground">
          Manage your subscription and billing details
        </p>
      </div>

      {/* Pending Downgrade Banner */}
      {pendingDowngrade && (
        <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-accent mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-foreground">Pending Downgrade</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Your plan will downgrade to <strong>{tierDetails[pendingDowngrade.toTier].name}</strong> on{' '}
                <strong>{formatDate(pendingDowngrade.effectiveDate)}</strong>.
                You will continue to have access to all {tierDetails[currentTier].name} features until then.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={handleCancelDowngrade}
              >
                Cancel Downgrade
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Current Plan */}
      <div className="bg-card text-card-foreground rounded-lg border p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold">Current Plan</h2>
            <p className="text-2xl font-bold text-primary mt-1">
              {tierDetails[currentTier].name}
            </p>
            <p className="text-muted-foreground">
              {tierDetails[currentTier].price}/month
            </p>
          </div>
          <div className="flex items-center gap-4">
            {currentTier !== 'free' && (
              <Button variant="outline" onClick={handleManageBilling}>
                <ExternalLink className="h-4 w-4 mr-2" />
                Manage Billing
              </Button>
            )}
            <CreditCard className="h-10 w-10 text-muted-foreground" />
          </div>
        </div>
      </div>

      {/* Active Pro grant banner (invite code / founder) */}
      {activeGrant && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Gift className="h-5 w-5 text-primary mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-foreground">
                Pro unlocked{activeGrant.source === 'invite_code' ? ' via invite code' : ''}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {activeGrant.expires_at
                  ? <>Free until <strong>{formatDate(new Date(activeGrant.expires_at).getTime())}</strong>. After that your plan reverts to Free — your existing photos are kept.</>
                  : <>Complimentary Pro access with no expiry.</>}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Redeem invite code (free-tier users only) */}
      {currentTier === 'free' && (
        <div className="bg-card text-card-foreground rounded-lg border p-6">
          <div className="flex items-center gap-2 mb-1">
            <Ticket className="h-5 w-5 text-primary" />
            <h2 className="font-semibold">Have an invite code?</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Redeem a Pro invite code to unlock Pro features free for a limited time.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={inviteCode}
              onChange={e => setInviteCode(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') void handleRedeemCode() }}
              placeholder="PRO-XXXX-XXXX"
              className="flex-1 px-3 py-2 border border-input rounded-lg font-mono uppercase focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <Button onClick={handleRedeemCode} disabled={redeemMutation.isPending || !inviteCode.trim()} className="gap-2">
              {redeemMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
              Redeem
            </Button>
          </div>
        </div>
      )}

      {/* Subscription Status (Webhook) */}
      {(subscriptionState.subscriptionId || lastWebhookEvent) && (
        <div className="bg-card text-card-foreground rounded-lg border p-6">
          <div className="flex items-center gap-2 mb-4">
            <Webhook className="h-5 w-5 text-indigo-600" />
            <h2 className="font-semibold">Subscription Status</h2>
            <span className={`ml-auto px-2 py-1 text-xs rounded-full ${
              subscriptionState.status === 'active' ? 'bg-secondary/15 text-secondary' :
              subscriptionState.status === 'past_due' ? 'bg-accent/15 text-accent' :
              subscriptionState.status === 'canceled' ? 'bg-destructive/15 text-destructive' :
              'bg-muted text-muted-foreground'
            }`}>
              {subscriptionState.status.replace('_', ' ').toUpperCase()}
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-4 text-sm">
            {subscriptionState.subscriptionId && (
              <div>
                <span className="text-muted-foreground">Subscription ID:</span>
                <span className="ml-2 font-mono text-xs">{subscriptionState.subscriptionId}</span>
              </div>
            )}
            {subscriptionState.currentPeriodEnd && (
              <div>
                <span className="text-muted-foreground">Current Period Ends:</span>
                <span className="ml-2">{new Date(subscriptionState.currentPeriodEnd * 1000).toLocaleDateString()}</span>
              </div>
            )}
            {subscriptionState.cancelAtPeriodEnd && subscriptionState.gracePeriodEndsAt && (
              <div className="col-span-2">
                <div className="flex items-center gap-2 p-3 bg-accent/10 border border-accent/20 rounded-lg">
                  <Clock className="h-4 w-4 text-accent" />
                  <span className="text-muted-foreground">
                    Grace period active until {new Date(subscriptionState.gracePeriodEndsAt).toLocaleDateString()}.
                    Your plan will downgrade to Free after this date.
                  </span>
                </div>
              </div>
            )}
          </div>

          {lastWebhookEvent && (
            <div className="mt-4 p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">Last Webhook Event:</p>
              <p className="font-mono text-sm">{lastWebhookEvent.type}</p>
              <p className="text-xs text-muted-foreground">
                {new Date(lastWebhookEvent.created * 1000).toLocaleString()}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Plan Selection */}
      <div>
        <h2 className="font-semibold mb-4">Available Plans</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {(Object.entries(tierDetails) as [OrgTier, typeof tierDetails.free][]).map(([tier, details]) => {
            const isCurrentPlan = currentTier === tier
            const isDowngrade = getTierLevel(tier) < getTierLevel(currentTier)
            const isPendingDowngradeTier = pendingDowngrade?.toTier === tier

            return (
              <div
                key={tier}
                className={`bg-card text-card-foreground rounded-lg border p-6 ${
                  isCurrentPlan ? 'border-primary ring-2 ring-primary ring-opacity-50' : ''
                } ${isPendingDowngradeTier ? 'border-accent/30 bg-accent/10' : ''}`}
              >
                {isCurrentPlan && (
                  <span className="inline-block px-2 py-1 bg-primary text-primary-foreground text-xs rounded-full mb-4">
                    Current Plan
                  </span>
                )}
                {isPendingDowngradeTier && !isCurrentPlan && (
                  <span className="inline-block px-2 py-1 bg-accent/20 text-foreground text-xs rounded-full mb-4">
                    Pending
                  </span>
                )}
                <h3 className="text-lg font-semibold">{details.name}</h3>
                <p className="text-3xl font-bold mt-2">
                  {details.price}
                  <span className="text-sm font-normal text-muted-foreground">/mo</span>
                </p>
                <ul className="mt-4 space-y-2">
                  {details.features.map((feature, i) => (
                    <li key={i} className="flex items-center text-sm">
                      <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                {!isCurrentPlan && !isPendingDowngradeTier && (
                  <Button
                    className="w-full mt-6"
                    variant={tier === 'business' ? 'default' : 'outline'}
                    onClick={() => isDowngrade ? handleDowngradeClick(tier) : handleUpgradeClick(tier)}
                  >
                    {isDowngrade ? 'Downgrade' : 'Upgrade'}
                  </Button>
                )}
                {isPendingDowngradeTier && !isCurrentPlan && (
                  <p className="text-sm text-muted-foreground text-center mt-6">
                    Scheduled for {formatDate(pendingDowngrade!.effectiveDate)}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Downgrade Modal */}
      {downgradeModalOpen && targetTier && (
        <DowngradeModal
          currentTier={currentTier}
          targetTier={targetTier}
          gracePeriodDays={GRACE_PERIOD_DAYS}
          onConfirm={async () => {
            await requestDowngrade(targetTier, GRACE_PERIOD_DAYS)
            setDowngradeModalOpen(false)
            toast({
              title: 'Downgrade scheduled',
              description: `Your plan will downgrade to ${tierDetails[targetTier].name} in ${GRACE_PERIOD_DAYS} days.`,
            })
          }}
          onCancel={() => setDowngradeModalOpen(false)}
        />
      )}

      {/* Stripe Checkout Modal */}
      {checkoutModalOpen && targetTier && (
        <StripeCheckoutModal
          tier={targetTier}
          onComplete={() => handleCheckoutComplete(targetTier)}
          onCancel={() => setCheckoutModalOpen(false)}
        />
      )}

      {/* Stripe Billing Portal Modal */}
      {billingPortalOpen && (
        <StripeBillingPortalModal
          currentTier={currentTier}
          onClose={() => setBillingPortalOpen(false)}
          onCancelSubscription={(withGracePeriod: boolean) => {
            if (withGracePeriod) {
              // Schedule cancellation at end of billing period (grace period)
              const newState = simulateAndProcessWebhook('customer.subscription.updated', {
                tier: currentTier as 'pro' | 'business',
                status: 'active',
                cancelAtPeriodEnd: true,
              })
              console.log('[Webhook] Subscription cancellation scheduled:', newState)
              setBillingPortalOpen(false)
              // Toast handled by webhook listener
            } else {
              // Immediate cancellation
              const newState = simulateAndProcessWebhook('customer.subscription.deleted', {
                status: 'canceled',
              })
              console.log('[Webhook] Subscription immediately cancelled:', newState)
              setOrgTier('free')
              setBillingPortalOpen(false)
              // Toast handled by webhook listener
            }
          }}
        />
      )}

      {/* Payment Method */}
      <div className="bg-card text-card-foreground rounded-lg border p-6">
        <h2 className="font-semibold mb-4">Payment Method</h2>
        {currentTier === 'free' ? (
          <div className="text-center py-4">
            <Zap className="h-8 w-8 text-muted-foreground mx-auto" />
            <p className="text-muted-foreground mt-2">
              Upgrade to a paid plan to add a payment method
            </p>
          </div>
        ) : (
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-8 bg-gradient-to-r from-blue-600 to-blue-400 rounded flex items-center justify-center text-white text-xs font-bold">
                VISA
              </div>
              <div>
                <p className="font-medium">•••• •••• •••• 4242</p>
                <p className="text-sm text-muted-foreground">Expires 12/25</p>
              </div>
            </div>
            <Button variant="outline" size="sm">Update</Button>
          </div>
        )}
      </div>

      {/* Billing History */}
      <div className="bg-card text-card-foreground rounded-lg border p-6">
        <h2 className="font-semibold mb-4">Billing History</h2>
        {currentTier === 'free' ? (
          <p className="text-muted-foreground text-center py-4">
            No billing history on the free plan
          </p>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between py-2 border-b">
              <div>
                <p className="font-medium">January 2025</p>
                <p className="text-sm text-muted-foreground">{tierDetails[currentTier].name} Plan</p>
              </div>
              <div className="text-right">
                <p className="font-medium">{tierDetails[currentTier].price}</p>
                <span className="text-xs text-green-600">Paid</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Helper to determine tier level for comparison
function getTierLevel(tier: OrgTier): number {
  const levels: Record<OrgTier, number> = {
    free: 0,
    pro: 1,
    business: 2,
  }
  return levels[tier]
}

// Downgrade confirmation modal for demo-mode downgrade scheduling.
interface DowngradeModalProps {
  currentTier: OrgTier
  targetTier: OrgTier
  gracePeriodDays: number
  onConfirm: () => Promise<void>
  onCancel: () => void
}

function DowngradeModal({
  currentTier,
  targetTier,
  gracePeriodDays,
  onConfirm,
  onCancel,
}: DowngradeModalProps) {
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const effectiveDate = new Date(Date.now() + gracePeriodDays * 24 * 60 * 60 * 1000)
  const formattedDate = effectiveDate.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    setIsSubmitting(true)
    try {
      await onConfirm()
    } catch {
      setError('Failed to process downgrade. Please try again.')
    }
    setIsSubmitting(false)
  }

  // Features that will be lost
  const currentFeatures = tierDetails[currentTier].features
  const targetFeatures = tierDetails[targetTier].features
  const lostFeatures = currentFeatures.filter(f => !targetFeatures.includes(f))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} />

      {/* Modal */}
      <div className="relative bg-card text-card-foreground rounded-lg border shadow-lg max-w-md w-full mx-4 p-6 z-10">
        <button
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          onClick={onCancel}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-3 text-foreground mb-4">
          <AlertTriangle className="h-6 w-6 text-accent" />
          <h2 className="text-lg font-semibold">Downgrade Plan</h2>
        </div>

        {/* Grace period warning */}
        <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-accent mt-0.5" />
            <div>
              <h3 className="font-medium text-foreground">Grace Period</h3>
              <p className="text-sm text-muted-foreground mt-1">
                You will continue to have access to all {tierDetails[currentTier].name} features
                until <strong>{formattedDate}</strong>. After that, your plan will be downgraded
                to {tierDetails[targetTier].name}.
              </p>
            </div>
          </div>
        </div>

        {/* Features you'll lose */}
        {lostFeatures.length > 0 && (
          <div className="mb-4">
            <p className="text-sm font-medium text-muted-foreground mb-2">
              Features you'll lose:
            </p>
            <ul className="space-y-1">
              {lostFeatures.map((feature, i) => (
                <li key={i} className="flex items-center text-sm text-destructive">
                  <X className="h-4 w-4 mr-2 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Error message */}
          {error && <p className="text-sm text-destructive">{error}</p>}

          {/* Action buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              className="flex-1 bg-amber-600 hover:bg-amber-700"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Processing...' : 'Confirm Downgrade'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Stripe Checkout Modal - simulates Stripe checkout for demo purposes
interface StripeCheckoutModalProps {
  tier: OrgTier
  onComplete: () => void
  onCancel: () => void
}

function StripeCheckoutModal({ tier, onComplete, onCancel }: StripeCheckoutModalProps) {
  const [cardNumber, setCardNumber] = useState('')
  const [expiry, setExpiry] = useState('')
  const [cvc, setCvc] = useState('')
  const [name, setName] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState('')
  const [step, setStep] = useState<'form' | 'processing' | 'success'>('form')

  const details = tierDetails[tier]

  // Format card number with spaces
  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '')
    const groups = cleaned.match(/.{1,4}/g)
    return groups ? groups.join(' ').substr(0, 19) : ''
  }

  // Format expiry as MM/YY
  const formatExpiry = (value: string) => {
    const cleaned = value.replace(/\D/g, '')
    if (cleaned.length >= 2) {
      return cleaned.substr(0, 2) + '/' + cleaned.substr(2, 2)
    }
    return cleaned
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validate inputs
    const cleanCardNumber = cardNumber.replace(/\s/g, '')
    if (cleanCardNumber.length !== 16) {
      setError('Please enter a valid card number')
      return
    }
    if (expiry.length !== 5) {
      setError('Please enter a valid expiry date (MM/YY)')
      return
    }
    if (cvc.length < 3) {
      setError('Please enter a valid CVC')
      return
    }
    if (!name.trim()) {
      setError('Please enter the cardholder name')
      return
    }

    setStep('processing')
    setIsProcessing(true)

    // Simulate Stripe payment processing
    console.log('[Demo] Initiating Stripe checkout...')
    console.log('[Demo] Card:', cleanCardNumber.slice(0, 4) + ' **** **** ' + cleanCardNumber.slice(-4))

    await new Promise(resolve => setTimeout(resolve, 2000))

    console.log('[Demo] Payment successful!')
    console.log('[Demo] Upgrading to', tier, 'plan')

    setStep('success')

    // Brief delay to show success before closing
    await new Promise(resolve => setTimeout(resolve, 1500))

    setIsProcessing(false)
    onComplete()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={step === 'form' ? onCancel : undefined} />

      {/* Modal */}
      <div className="relative bg-card text-card-foreground rounded-lg border shadow-xl max-w-md w-full mx-4 z-10 overflow-hidden">
        {/* Stripe-style header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              <span className="font-semibold">Secure Checkout</span>
            </div>
            {step === 'form' && (
              <button onClick={onCancel} className="text-white/80 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          <div className="mt-4">
            <p className="text-white/80 text-sm">Subscribe to</p>
            <p className="text-2xl font-bold">{details.name} Plan</p>
            <p className="text-white/90">{details.price}/month</p>
          </div>
        </div>

        <div className="p-6">
          {step === 'form' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Card Number */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Card number</label>
                <div className="relative">
                  <input
                    type="text"
                    value={cardNumber}
                    onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                    placeholder="1234 5678 9012 3456"
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    maxLength={19}
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 flex gap-1">
                    <div className="w-8 h-5 bg-gradient-to-r from-blue-600 to-blue-400 rounded text-white text-[8px] flex items-center justify-center font-bold">VISA</div>
                    <div className="w-8 h-5 bg-gradient-to-r from-orange-500 to-yellow-400 rounded text-white text-[8px] flex items-center justify-center font-bold">MC</div>
                  </div>
                </div>
              </div>

              {/* Expiry and CVC */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5">Expiry</label>
                  <input
                    type="text"
                    value={expiry}
                    onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                    placeholder="MM/YY"
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    maxLength={5}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">CVC</label>
                  <input
                    type="text"
                    value={cvc}
                    onChange={(e) => setCvc(e.target.value.replace(/\D/g, '').substr(0, 4))}
                    placeholder="123"
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    maxLength={4}
                  />
                </div>
              </div>

              {/* Cardholder Name */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Cardholder name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                />
              </div>

              {/* Error */}
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 p-3 rounded-lg">
                  {error}
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                className="w-full py-3 bg-indigo-600 hover:bg-indigo-700"
                disabled={isProcessing}
              >
                Pay {details.price}/month
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                Test card: 4242 4242 4242 4242, any future expiry, any CVC
              </p>
            </form>
          )}

          {step === 'processing' && (
            <div className="py-8 text-center">
              <Loader2 className="h-12 w-12 animate-spin text-indigo-600 mx-auto" />
              <p className="mt-4 font-medium">Processing payment...</p>
              <p className="text-sm text-muted-foreground mt-1">Please wait while we process your payment</p>
            </div>
          )}

          {step === 'success' && (
            <div className="py-8 text-center">
              <div className="w-16 h-16 bg-secondary/15 rounded-full flex items-center justify-center mx-auto">
                <Check className="h-8 w-8 text-secondary" />
              </div>
              <p className="mt-4 font-medium text-lg">Payment successful!</p>
              <p className="text-sm text-muted-foreground mt-1">
                Welcome to the {details.name} plan. Redirecting...
              </p>
            </div>
          )}
        </div>

        {/* Security footer */}
        <div className="bg-muted/50 px-6 py-3 border-t">
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Lock className="h-3 w-3" />
            <span>Powered by Stripe (Demo Mode)</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// Stripe Billing Portal Modal - simulates Stripe billing portal for demo purposes
interface StripeBillingPortalModalProps {
  currentTier: OrgTier
  onClose: () => void
  onCancelSubscription: (withGracePeriod: boolean) => void
}

function StripeBillingPortalModal({ currentTier, onClose, onCancelSubscription }: StripeBillingPortalModalProps) {
  const [activeTab, setActiveTab] = useState<'invoices' | 'payment' | 'cancel'>('invoices')
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [cancelType, setCancelType] = useState<'immediate' | 'end_of_period'>('end_of_period')
  const [isProcessing, setIsProcessing] = useState(false)
  const [newCardNumber, setNewCardNumber] = useState('')
  const [newExpiry, setNewExpiry] = useState('')
  const [newCvc, setNewCvc] = useState('')
  const [updateSuccess, setUpdateSuccess] = useState(false)

  const details = tierDetails[currentTier]

  // Demo invoices
  const invoices = [
    { id: 'inv_001', date: 'January 2025', amount: details.price, status: 'Paid' },
    { id: 'inv_002', date: 'December 2024', amount: details.price, status: 'Paid' },
    { id: 'inv_003', date: 'November 2024', amount: details.price, status: 'Paid' },
  ]

  // Format card number with spaces
  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '')
    const groups = cleaned.match(/.{1,4}/g)
    return groups ? groups.join(' ').substr(0, 19) : ''
  }

  // Format expiry as MM/YY
  const formatExpiry = (value: string) => {
    const cleaned = value.replace(/\D/g, '')
    if (cleaned.length >= 2) {
      return cleaned.substr(0, 2) + '/' + cleaned.substr(2, 2)
    }
    return cleaned
  }

  const handleUpdatePayment = async () => {
    const cleanCardNumber = newCardNumber.replace(/\s/g, '')
    if (cleanCardNumber.length !== 16 || newExpiry.length !== 5 || newCvc.length < 3) {
      return
    }

    setIsProcessing(true)
    console.log('[Demo] Updating payment method...')
    await new Promise(resolve => setTimeout(resolve, 1500))
    console.log('[Demo] Payment method updated!')
    setIsProcessing(false)
    setUpdateSuccess(true)
    setNewCardNumber('')
    setNewExpiry('')
    setNewCvc('')
    setTimeout(() => setUpdateSuccess(false), 3000)
  }

  const handleCancelSubscription = async () => {
    setIsProcessing(true)
    const withGracePeriod = cancelType === 'end_of_period'
    console.log(`[Demo] Cancelling subscription (grace period: ${withGracePeriod})...`)
    await new Promise(resolve => setTimeout(resolve, 1500))
    console.log('[Demo] Subscription cancellation processed!')
    setIsProcessing(false)
    onCancelSubscription(withGracePeriod)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-card text-card-foreground rounded-lg border shadow-xl max-w-2xl w-full mx-4 z-10 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Stripe-style header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              <span className="font-semibold">Billing Portal</span>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-4">
            <p className="text-white/80 text-sm">Managing subscription for</p>
            <p className="text-2xl font-bold">{details.name} Plan</p>
            <p className="text-white/90">{details.price}/month</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b flex-shrink-0">
          <div className="flex">
            <button
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'invoices'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setActiveTab('invoices')}
            >
              <FileText className="h-4 w-4 inline mr-2" />
              Invoices
            </button>
            <button
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'payment'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setActiveTab('payment')}
            >
              <CreditCard className="h-4 w-4 inline mr-2" />
              Payment Method
            </button>
            <button
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'cancel'
                  ? 'border-destructive text-destructive'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setActiveTab('cancel')}
            >
              <AlertTriangle className="h-4 w-4 inline mr-2" />
              Cancel Subscription
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Invoices Tab */}
          {activeTab === 'invoices' && (
            <div>
              <h3 className="font-semibold mb-4">Billing History</h3>
              <div className="space-y-2">
                {invoices.map((invoice) => (
                  <div
                    key={invoice.id}
                    className="flex items-center justify-between p-4 bg-muted/50 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{invoice.date}</p>
                        <p className="text-sm text-muted-foreground">{details.name} Plan</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-medium">{invoice.amount}</span>
                      <span className="text-xs px-2 py-1 bg-secondary/15 text-secondary rounded-full">
                        {invoice.status}
                      </span>
                      <Button variant="ghost" size="sm">
                        Download
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment Method Tab */}
          {activeTab === 'payment' && (
            <div>
              <h3 className="font-semibold mb-4">Current Payment Method</h3>
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-8 bg-gradient-to-r from-blue-600 to-blue-400 rounded flex items-center justify-center text-white text-xs font-bold">
                    VISA
                  </div>
                  <div>
                    <p className="font-medium">•••• •••• •••• 4242</p>
                    <p className="text-sm text-muted-foreground">Expires 12/25</p>
                  </div>
                </div>
                <span className="text-xs px-2 py-1 bg-secondary/15 text-secondary rounded-full">
                  Default
                </span>
              </div>

              <h3 className="font-semibold mb-4">Update Payment Method</h3>
              {updateSuccess && (
                <div className="mb-4 p-3 bg-secondary/10 text-foreground rounded-lg flex items-center gap-2">
                  <Check className="h-4 w-4 text-secondary" />
                  Payment method updated successfully!
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5">New card number</label>
                  <input
                    type="text"
                    value={newCardNumber}
                    onChange={(e) => setNewCardNumber(formatCardNumber(e.target.value))}
                    placeholder="1234 5678 9012 3456"
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    maxLength={19}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Expiry</label>
                    <input
                      type="text"
                      value={newExpiry}
                      onChange={(e) => setNewExpiry(formatExpiry(e.target.value))}
                      placeholder="MM/YY"
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                      maxLength={5}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">CVC</label>
                    <input
                      type="text"
                      value={newCvc}
                      onChange={(e) => setNewCvc(e.target.value.replace(/\D/g, '').substr(0, 4))}
                      placeholder="123"
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                      maxLength={4}
                    />
                  </div>
                </div>
                <Button
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  disabled={isProcessing || newCardNumber.replace(/\s/g, '').length !== 16}
                  onClick={handleUpdatePayment}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Update Payment Method'
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Cancel Subscription Tab */}
          {activeTab === 'cancel' && (
            <div>
              {!showCancelConfirm ? (
                <>
                  <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-accent mt-0.5" />
                      <div>
                        <h3 className="font-medium text-foreground">Before you cancel</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          If you cancel your subscription, you'll lose access to:
                        </p>
                        <ul className="mt-2 space-y-1">
                          {details.features.map((feature, i) => (
                            <li key={i} className="flex items-center text-sm text-muted-foreground">
                              <X className="h-4 w-4 mr-2 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Cancellation Type Options */}
                  <div className="mb-6 space-y-3">
                    <h4 className="font-medium">How would you like to cancel?</h4>
                    <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                      <input
                        type="radio"
                        name="cancelType"
                        value="end_of_period"
                        checked={cancelType === 'end_of_period'}
                        onChange={() => setCancelType('end_of_period')}
                        className="mt-1"
                      />
                      <div>
                        <span className="font-medium">Cancel at end of billing period (Recommended)</span>
                        <p className="text-sm text-muted-foreground mt-1">
                          Keep access to {details.name} features until the end of your current billing cycle.
                          Your subscription will not renew and you'll be downgraded to Free after the grace period.
                        </p>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                      <input
                        type="radio"
                        name="cancelType"
                        value="immediate"
                        checked={cancelType === 'immediate'}
                        onChange={() => setCancelType('immediate')}
                        className="mt-1"
                      />
                      <div>
                        <span className="font-medium">Cancel immediately</span>
                        <p className="text-sm text-muted-foreground mt-1">
                          Lose access to {details.name} features right away.
                          You will be downgraded to Free immediately.
                        </p>
                      </div>
                    </label>
                  </div>

                  <Button
                    variant="destructive"
                    className="w-full"
                    onClick={() => setShowCancelConfirm(true)}
                  >
                    Continue with Cancellation
                  </Button>
                </>
              ) : (
                <div className="text-center py-4">
                  <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Are you sure?</h3>
                  <p className="text-muted-foreground mb-4">
                    {cancelType === 'end_of_period' ? (
                      <>Your subscription will be cancelled at the end of your current billing period. You'll keep access to {details.name} features until then (grace period).</>
                    ) : (
                      <>Your subscription will be cancelled immediately and you'll lose access to {details.name} features right away.</>
                    )}
                  </p>
                  {cancelType === 'end_of_period' && (
                    <div className="bg-secondary/10 border border-secondary/20 rounded-lg p-3 mb-4 text-sm text-foreground">
                      <Clock className="h-4 w-4 inline mr-2 text-secondary" />
                      Grace period: You'll keep your features until the end of your billing cycle.
                    </div>
                  )}
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setShowCancelConfirm(false)}
                      disabled={isProcessing}
                    >
                      Keep Subscription
                    </Button>
                    <Button
                      variant="destructive"
                      className="flex-1"
                      onClick={handleCancelSubscription}
                      disabled={isProcessing}
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        cancelType === 'end_of_period' ? 'Schedule Cancellation' : 'Yes, Cancel Now'
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Security footer */}
        <div className="bg-muted/50 px-6 py-3 border-t flex-shrink-0">
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Lock className="h-3 w-3" />
            <span>Powered by Stripe (Demo Mode)</span>
          </div>
        </div>
      </div>
    </div>
  )
}
